import { useState, useEffect } from "react";
import { errorToast } from "../../utils/toasters";
import { handlePostRequest } from "../../hooks/api";
import GenericCardSkeleton from "../skeletons/GenericCardSkeleton";

const AllergenIcon = ({ icon, borderColor, alt }) => (
  <div
    className="flex items-center justify-center rounded-full bg-none font-afacad w-25 h-25 p-2"
    style={{ border: `1px solid ${borderColor}` }}
  >
    <img
      src={icon || "/placeholder.svg"}
      alt={alt}
      className="object-contain"
    />
  </div>
);

const AllergenItem = ({ icon, name, category, color, alt, isLeft = false }) => {
  return (
    <div className="flex items-center gap-4 w-full ">
      {isLeft ? (
        <>
          <div className="flex items-center justify-end w-full">
            <div className="text-right">
              <div className="text-gray-700 text-lg font-medium">{name}</div>
              <div className="text-sm font-medium" style={{ color }}>
                {category}
              </div>
            </div>
            <div className="flex flex-shrink-0 ml-2">
              <AllergenIcon icon={icon} borderColor={color} alt={alt} />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-shrink-0">
            <AllergenIcon icon={icon} borderColor={color} alt={alt} />
          </div>
          <div className="flex-grow">
            <div className="text-gray-700 text-lg font-medium">{name}</div>
            <div className="text-sm font-medium" style={{ color }}>
              {category}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const categoryToColor = {
  High: "#CA1C1E",
  Moderate: "#E9B949",
  Low: "#4EA737",
};

const allergenToIcon = {
  "Tree Pollen": "/images/tree.svg",
  "Ragweed Pollen": "/images/Ragweed.svg",
  Mold: "/images/Moss.svg",
  "Grass Pollen": "/images/Grass.svg",
};

export default function AllergyCard({
  backgroundImg = "/images/allergy_bg4.png",
}) {
  const [allergyData, setAllergyData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllergySummary = async () => {
      const data = {
        lat: "30.2711286",
        lang: "-97.7436995",
        geohash: localStorage.getItem("geohash") || "9v6m",
      };

      try {
        setIsProcessing(true);
        setError(null);

        const response = await handlePostRequest(
          "/news/allergySummary",
          data,
          {},
          false
        );

        setAllergyData(response);
      } catch (error) {
        setError(error.response?.data?.message ?? error.data?.message ?? error);
        errorToast("Error fetching allergy summary: " + error, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
        });
      } finally {
        setIsProcessing(false);
      }
    };

    fetchAllergySummary();
  }, []);

  const allergysummary = allergyData?.data.allergysummary ?? "";
  const polleninfo = allergyData?.data.polleninfo ?? [];
  const [title, description] = allergysummary.split("\n");

  const getAllergenItems = () => {
    if (!polleninfo || polleninfo.length === 0) {
      return [
        {
          icon: "/images/tree-icon.png",
          name: "Tree",
          category: "Low",
          alt: "Tree icon",
        },
        {
          icon: "/images/ragweed-icon.png",
          name: "Ragweed",
          category: "Medium",
          alt: "Ragweed icon",
        },
        {
          icon: "/images/mold-icon.png",
          name: "Mold",
          category: "Low",
          alt: "Mold icon",
        },
        {
          icon: "/images/grass-icon.png",
          name: "Grass",
          category: "High",
          alt: "Grass icon",
        },
      ];
    }

    return polleninfo.map((item) => ({
      icon: allergenToIcon[item.Name] || "/placeholder.svg",
      name: item.Name.replace(" Pollen", ""),
      category: item.Category,
      alt: `${item.Name} icon`,
    }));
  };

  const allergens = getAllergenItems();

  if (isProcessing) {
    return <GenericCardSkeleton />;
  }

  if (error) {
    return (
      <div className="w-full p-4 text-red-500">
        Error loading allergy data
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="rounded-2xl overflow-hidden shadow-sm border">
        <div className="relative">
          <div className="absolute inset-0">
            <div className="relative w-full h-full">
              <img
                src={backgroundImg}
                alt="Textured background"
                className="object-cover w-full h-full absolute"
              />
              <div
                className="absolute inset-0"
                style={{
                  backdropFilter: "blur(2px)",
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                }}
              />
            </div>
          </div>

          <div className="relative z-10 grid grid-cols-2 justify-center items-center">
            <div className="p-2 pr-7 ml-auto flex flex-col items-center justify-center">
              <AllergenItem
                icon={allergens[0]?.icon}
                name={allergens[0]?.name}
                category={allergens[0]?.category}
                color={categoryToColor[allergens[0]?.category]}
                alt={allergens[0]?.alt}
                isLeft={true}
              />
            </div>

            <div className="p-2 pl-7 flex flex-col items-center justify-center">
              <AllergenItem
                icon={allergens[1]?.icon}
                name={allergens[1]?.name}
                category={allergens[1]?.category}
                color={categoryToColor[allergens[1]?.category]}
                alt={allergens[1]?.alt}
              />
            </div>

            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-px h-[90%] bg-gray-300" />

            <div className="col-span-2 h-px w-[80%] bg-gray-300 mx-auto my-2" />

            <div className="p-2 pr-7 flex ml-auto flex-col items-center justify-center">
              <AllergenItem
                icon={allergens[2]?.icon}
                name={allergens[2]?.name}
                category={allergens[2]?.category}
                color={categoryToColor[allergens[2]?.category]}
                alt={allergens[2]?.alt}
                isLeft={true}
              />
            </div>

            <div className="p-2 pl-7 flex flex-col items-center justify-center">
              <AllergenItem
                icon={allergens[3]?.icon}
                name={allergens[3]?.name}
                category={allergens[3]?.category}
                color={categoryToColor[allergens[3]?.category]}
                alt={allergens[3]?.alt}
              />
            </div>
          </div>
        </div>

        <div className="p-4 bg-white">
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="font-medium text-gray-800">
                {title || "Allergy Summary"}
              </div>
              <p className="text-gray-600 text-sm mt-1">
                {description || "Current allergy information for your area"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
